/**
 * IL TESTO CHE L'UTENTE LEGGE QUANDO QUALCOSA FALLISCE — e perché non è `error.message`.
 *
 * Un errore HTTP il suo messaggio ce l'ha: `JsonApiHttpError.getUserMessage()` lo costruisce
 * dal `meta.code` del BE e sa dire cosa fare. Ma **il caso peggiore non è un errore HTTP**: è
 * quello in cui una risposta non arriva affatto. Lì `fetch` solleva un `TypeError` il cui
 * messaggio è deciso dal browser — «Failed to fetch» su Chrome, «Load failed» su Safari,
 * «NetworkError when attempting to fetch resource» su Firefox — e quel testo finiva a schermo
 * tale e quale dentro un alert rosso.
 *
 * ⚠️ È il messaggio meno utile possibile: è in inglese, nomina una funzione del browser, e
 * soprattutto **non dice la cosa che conta** — cioè se il problema è la rete di chi guarda
 * (allora riprovare fra un minuto ha senso) o il server (allora riprovare non serve e va
 * segnalato). Caso reale, 25-09-2026: un errore del BE su `/organization/company-data?
 * section=branding` mostrava «Impossibile leggere il branding / Failed to fetch».
 *
 * ⚠️ E «Failed to fetch» **non vuol dire «sei offline»**, che è l'equivoco comodo: la stessa
 * eccezione esce quando il server crolla *prima* di aggiungere gli header CORS, cioè su un 500
 * vero. Il browser non ha potuto leggere la risposta, quindi per lui è come se non ci fosse
 * stata. Per questo le due frasi si scelgono chiedendo alla rete (`offline()`, che sa anche
 * riconoscere il Wi-Fi col portale) e non dal testo dell'eccezione.
 */
import { JsonApiHttpError } from './jsonapi/errors';
import { offline } from '@/shared/lib/net';

/**
 * Una richiesta che non ha avuto risposta: rete assente, DNS, timeout, connessione chiusa,
 * o server caduto prima degli header CORS. Sostituisce il `TypeError` del browser così il
 * riconoscimento è per tipo e non per stringa — le stringhe le decide il browser, e cambiano.
 */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Richiesta senza risposta dal sistema', { cause });
    this.name = 'NetworkError';
  }

  getUserMessage(): string {
    return offline()
      ? 'Sei senza connessione: la richiesta non è nemmeno partita. Riprova quando la rete torna.'
      : 'Il sistema non ha risposto. Non dipende da quello che hai inserito: riprova fra qualche istante e, se si ripete, segnalalo.';
  }
}

/** Testi con cui i browser annunciano una `fetch` andata a vuoto. */
const BROWSER_NETWORK_MESSAGES = /failed to fetch|load failed|networkerror|network request failed|^network error$/i;

/**
 * È un fallimento di rete? Vale anche per gli errori nati fuori dal nostro client HTTP —
 * un `TypeError` di una `fetch` altrui, o quello che un browser futuro chiamerà in un altro modo.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (error instanceof NetworkError) return true;
  return error instanceof Error && BROWSER_NETWORK_MESSAGES.test(error.message);
}

/** La richiesta è stata annullata da noi (cambio pagina, `AbortController`, timeout della sonda). */
function isAborted(error: unknown): boolean {
  return error instanceof Error && (error.name === 'AbortError' || error.name === 'TimeoutError');
}

/**
 * Il messaggio da mostrare per un errore qualsiasi, in ordine di specificità:
 * errore del BE con un codice noto → fallimento di rete → richiesta annullata → `message`.
 *
 * `fallback` è per chi ha una frase migliore del generico quando l'errore non è un `Error`
 * (es. «Errore durante l'export»).
 */
export function userFacingMessage(error: unknown, fallback = 'Si è verificato un errore imprevisto.'): string {
  if (error instanceof JsonApiHttpError) return error.getUserMessage();
  if (isNetworkFailure(error)) return new NetworkError(error).getUserMessage();
  if (isAborted(error)) return 'Richiesta interrotta prima che arrivasse la risposta. Riprova.';
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === 'string' && error) return error;
  return fallback;
}
